import { SxProps, Theme } from "@mui/material";

export const gradientText: SxProps<Theme> = {
  background: "linear-gradient(90deg, #3b82f6 0%, #38bdf8 60%, #67e8f9 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
};

export const eyebrowSx: SxProps<Theme> = {
  fontSize: "0.7rem",
  fontWeight: 600,
  letterSpacing: "0.2em",
  textTransform: "uppercase",
  color: "#38bdf8",
  fontFamily: "'Barlow', sans-serif",
  mb: 3,
};

export const glassmorphismCard: SxProps<Theme> = {
  bgcolor: "rgba(5,12,35,0.35)",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  border: "1px solid rgba(56,189,248,0.18)",
  borderRadius: "20px",
  boxShadow: "0 8px 40px rgba(10,15,30,0.3), 0 0 40px rgba(56,189,248,0.08)",
};

export const heroOuter: SxProps<Theme> = {
  position: "relative",
  overflow: "hidden",
  color: "white",
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  background: "#000",
};

export const heroIframe: SxProps<Theme> = {
  position: "absolute",
  top: "50%",
  left: "50%",
  width: "177.78vh",
  minWidth: "100%",
  height: "56.25vw",
  minHeight: "100%",
  transform: "translate(-50%, -50%)",
  border: "none",
  zIndex: 0,
  pointerEvents: "none",
};

export const heroRadialOverlay: SxProps<Theme> = {
  position: "absolute",
  inset: 0,
  background:
    "radial-gradient(ellipse 60% 70% at 50% 50%, rgba(5,12,35,0.92) 0%, rgba(5,12,35,0.48) 55%, rgba(5,12,35,0.05) 100%)",
  zIndex: 1,
  pointerEvents: "none",
};

export const heroBottomGradient: SxProps<Theme> = {
  position: "absolute",
  bottom: 0,
  left: 0,
  width: "100%",
  height: "200px",
  background: "linear-gradient(to bottom, transparent, #050c23)",
  zIndex: 1,
  pointerEvents: "none",
};

export const heroEyebrow: SxProps<Theme> = {
  ...eyebrowSx,
  opacity: 0,
  animation: "fadeUp 0.7s ease forwards",
  animationDelay: "0s",
  "@keyframes fadeUp": {
    from: { opacity: 0, transform: "translateY(28px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
};

export const heroTitleLine: SxProps<Theme> = {
  fontSize: { xs: "2.8rem", md: "4.5rem", lg: "5.5rem" },
  letterSpacing: "-0.02em",
  lineHeight: 1.1,
  fontWeight: 800,
  fontFamily: "'Poppins', sans-serif",
  color: "white",
  mb: 0,
  opacity: 0,
  animation: "fadeUp 0.7s ease forwards",
  animationDelay: "0.2s",
};

export const heroTitleLineTwo: SxProps<Theme> = {
  fontSize: { xs: "2.8rem", md: "4.5rem", lg: "5.5rem" },
  letterSpacing: "-0.02em",
  lineHeight: 1.1,
  fontWeight: 800,
  fontFamily: "'Poppins', sans-serif",
  color: "white",
  mb: 5,
  opacity: 0,
  animation: "fadeUp 0.7s ease forwards",
  animationDelay: "0.35s",
};

export const heroSubtitle: SxProps<Theme> = {
  mb: 6,
  fontWeight: 400,
  color: "rgba(255,255,255,0.82)",
  lineHeight: 1.65,
  fontSize: { xs: "1rem", md: "1.15rem" },
  maxWidth: 640,
  mx: "auto",
  fontFamily: "'Barlow', sans-serif",
  opacity: 0,
  animation: "fadeUp 0.7s ease forwards",
  animationDelay: "0.5s",
};

export const heroStatsRow: SxProps<Theme> = {
  display: "flex",
  gap: { xs: 4, md: 6 },
  justifyContent: "center",
  flexWrap: "wrap",
  mt: 2,
  mb: 6,
  opacity: 0,
  animation: "fadeUp 0.7s ease forwards",
  animationDelay: "0.65s",
};

export const statNumber: SxProps<Theme> = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 800,
  fontSize: { xs: "1.8rem", md: "2.4rem" },
  lineHeight: 1,
  mb: 0.5,
  color: "white",
};

export const statLabel: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  fontSize: "0.8rem",
  fontWeight: 600,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.65)",
};

export const heroCtaRow: SxProps<Theme> = {
  display: "flex",
  gap: 2,
  flexWrap: "wrap",
  justifyContent: "center",
  opacity: 0,
  animation: "fadeUp 0.7s ease forwards",
  animationDelay: "0.8s",
};

export const scrollIndicator: SxProps<Theme> = {
  position: "absolute",
  bottom: 20,
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 3,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 0.75,
  cursor: "pointer",
  "@keyframes float": {
    "0%, 100%": { transform: "translateX(-50%) translateY(0)" },
    "50%": { transform: "translateX(-50%) translateY(-8px)" },
  },
  animation: "float 2.4s ease-in-out infinite",
  "&:hover": { opacity: 0.7 },
  transition: "opacity 0.2s",
};

export const scrollLabel: SxProps<Theme> = {
  fontSize: "0.65rem",
  fontWeight: 600,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.5)",
  fontFamily: "'Barlow', sans-serif",
};

export const benefitCardTitle: SxProps<Theme> = {
  fontWeight: 700,
  color: "white",
  mb: 1.5,
  fontFamily: "'Barlow', sans-serif",
};

export const benefitCardBody: SxProps<Theme> = {
  color: "rgba(255,255,255,0.82)",
  lineHeight: 1.65,
  fontFamily: "'Barlow', sans-serif",
  fontSize: "0.95rem",
};

export const benefitsOuter: SxProps<Theme> = {
  py: { xs: 12, md: 18 },
  background: "linear-gradient(to bottom, #050c23, #080c14)",
};

export const benefitsSectionTitle: SxProps<Theme> = {
  color: "white",
  fontSize: { xs: "2.5rem", md: "3.5rem", lg: "4.5rem" },
  fontWeight: 800,
  letterSpacing: "-0.02em",
  lineHeight: 1.15,
  fontFamily: "'Poppins', sans-serif",
  mb: 3,
};

export const benefitsSectionSubtitle: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  color: "rgba(255,255,255,0.75)",
  fontSize: { xs: "1rem", md: "1.1rem" },
  lineHeight: 1.7,
  maxWidth: 600,
  mx: "auto",
};

export const processStepNumber: SxProps<Theme> = {
  fontSize: { xs: "3.5rem", md: "4.5rem" },
  fontWeight: 800,
  fontFamily: "'Barlow', sans-serif",
  lineHeight: 1,
  mb: 2,
  ...gradientText,
};

export const processStepTitle: SxProps<Theme> = {
  fontWeight: 700,
  color: "white",
  mb: 1.5,
  fontFamily: "'Barlow', sans-serif",
};

export const processStepDescription: SxProps<Theme> = {
  color: "rgba(255,255,255,0.82)",
  lineHeight: 1.65,
  fontFamily: "'Barlow', sans-serif",
  fontSize: "0.95rem",
};

export const howSolarWorksOuter: SxProps<Theme> = {
  py: { xs: 12, md: 18 },
  position: "relative",
  backgroundImage: "url('/img/assets/bedroom.jpg')",
  backgroundSize: "cover",
  backgroundPosition: "center",
  overflow: "hidden",
};

export const howSolarWorksOverlay: SxProps<Theme> = {
  position: "absolute",
  inset: 0,
  background: "linear-gradient(to bottom, #080c14 0%, rgba(8,12,20,0.25) 25%, rgba(8,12,20,0.25) 75%, #0a0f1e 100%)",
  zIndex: 0,
  pointerEvents: "none",
};

export const howSolarWorksRadialOverlay: SxProps<Theme> = {
  position: "absolute",
  inset: 0,
  background: "radial-gradient(ellipse 80% 60% at 50% 50%, rgba(5,12,35,0.18) 0%, transparent 100%)",
  zIndex: 0,
  pointerEvents: "none",
};

export const sectionTitle: SxProps<Theme> = {
  color: "white",
  fontSize: { xs: "2.5rem", md: "3.5rem", lg: "4.5rem" },
  fontWeight: 800,
  letterSpacing: "-0.02em",
  lineHeight: 1.15,
  fontFamily: "'Poppins', sans-serif",
};

export const processStepsRow: SxProps<Theme> = {
  display: "flex",
  flexDirection: { xs: "column", md: "row" },
  gap: { xs: 6, md: 0 },
  position: "relative",
};

export const processStepsLine: SxProps<Theme> = {
  display: { xs: "none", md: "block" },
  position: "absolute",
  top: "40px",
  left: "12.5%",
  right: "12.5%",
  height: "1px",
  background: "linear-gradient(to right, rgba(56,189,248,0.15), rgba(56,189,248,0.35), rgba(56,189,248,0.15))",
  zIndex: 0,
};

export const savingsOuter: SxProps<Theme> = {
  py: { xs: 12, md: 18 },
  background: "linear-gradient(to bottom, #0a0f1e, #0c1120)",
};

export const savingsSectionTitle: SxProps<Theme> = {
  color: "white",
  fontSize: { xs: "2.5rem", md: "3.5rem", lg: "4rem" },
  fontWeight: 800,
  letterSpacing: "-0.02em",
  lineHeight: 1.15,
  fontFamily: "'Poppins', sans-serif",
  mb: 4,
};

export const savingsDescription: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  color: "rgba(255,255,255,0.82)",
  fontSize: { xs: "1rem", md: "1.1rem" },
  lineHeight: 1.7,
  mb: 4,
};

export const bulletText: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  color: "rgba(255,255,255,0.82)",
  fontSize: "1rem",
  lineHeight: 1.6,
};

export const sliderLabelRow: SxProps<Theme> = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  mb: 1,
  mt: 5,
};

export const sliderLabelTitle: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  fontWeight: 700,
  color: "white",
  fontSize: "0.95rem",
};

export const sliderLabelValue: SxProps<Theme> = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 800,
  fontSize: "1.3rem",
  ...gradientText,
};

export const slider: SxProps<Theme> = {
  color: "#38bdf8",
  "& .MuiSlider-thumb": {
    width: 24,
    height: 24,
    background: "linear-gradient(135deg, #3b82f6, #38bdf8)",
    boxShadow: "0 0 12px rgba(56,189,248,0.5)",
    "&:hover": { boxShadow: "0 0 20px rgba(56,189,248,0.7)" },
  },
  "& .MuiSlider-track": {
    background: "linear-gradient(to right, #3b82f6, #38bdf8)",
    border: "none",
    height: 10,
  },
  "& .MuiSlider-rail": {
    background:
      "linear-gradient(to bottom, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.55) 50%, rgba(255,255,255,0.28) 100%)",
    height: 10,
    borderRadius: "10px",
    boxShadow: "inset 0 2px 4px rgba(0,0,0,0.25)",
  },
};

export const sliderRangeLabel: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  color: "rgba(255,255,255,0.45)",
  fontSize: "0.8rem",
};

export const comparisonCardHeader: SxProps<Theme> = {
  px: 4,
  py: 2.5,
  bgcolor: "rgba(5,12,35,0.85)",
  background: "linear-gradient(135deg, rgba(59,130,246,0.15) 0%, rgba(56,189,248,0.08) 100%)",
  borderBottom: "1px solid rgba(56,189,248,0.12)",
};

export const comparisonCardHeaderLabel: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  fontSize: "0.7rem",
  fontWeight: 700,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.55)",
};

export const utilityRow: SxProps<Theme> = {
  pl: 3,
  pr: 4,
  py: 4,
  borderBottom: "1px solid rgba(56,189,248,0.08)",
  borderLeft: "3px solid #F4C751",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

export const rowTitle: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  fontWeight: 700,
  color: "white",
  fontSize: "1rem",
  mb: 0.25,
};

export const rowSubtitle: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  color: "rgba(255,255,255,0.7)",
  fontSize: "0.82rem",
};

export const utilityPrice: SxProps<Theme> = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 800,
  fontSize: "2.4rem",
  color: "#F4C751",
  lineHeight: 1,
};

export const priceUnit: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  color: "rgba(255,255,255,0.5)",
  fontSize: "0.85rem",
  ml: 0.5,
};

export const solarRow: SxProps<Theme> = {
  pl: 3,
  pr: 4,
  py: 4,
  borderBottom: "1px solid rgba(56,189,248,0.08)",
  borderLeft: "3px solid #51F2CB",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

export const solarPrice: SxProps<Theme> = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 800,
  fontSize: "2.4rem",
  color: "#51F2CB",
  lineHeight: 1,
};

export const savingsTotalRow: SxProps<Theme> = {
  pl: 3,
  pr: 4,
  py: 4,
  background: "linear-gradient(135deg, rgba(81,242,203,0.06) 0%, rgba(56,189,248,0.04) 100%)",
  borderLeft: "3px solid #51F2CB",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

export const savingsTotalPrice: SxProps<Theme> = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 800,
  fontSize: "2.4rem",
  color: "#20C2F1",
  lineHeight: 1,
};

export const comparisonCardGradientBar: SxProps<Theme> = {
  height: "3px",
  background: "linear-gradient(to right, #3b82f6, #38bdf8, #51F2CB)",
  borderRadius: "20px 20px 0 0",
};

export const comparisonDisclaimer: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  color: "rgba(255,255,255,0.4)",
  fontSize: "0.8rem",
  fontStyle: "italic",
};

export const videoFeatureOuter: SxProps<Theme> = {
  minHeight: "100vh",
  background: "linear-gradient(to bottom, #0c1120, #050c23 20%)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  py: { xs: 12, md: 18 },
};

export const videoIframe: SxProps<Theme> = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  border: "none",
};

export const videoThumbnail: SxProps<Theme> = {
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundImage: "url('/img/assets/Homeonwer_Thumb.jpg')",
  backgroundSize: "cover",
  backgroundPosition: "center",
};

export const videoPlayOverlay: SxProps<Theme> = {
  position: "absolute",
  inset: 0,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&:hover .play-btn": {
    transform: "scale(1.1)",
    backgroundColor: "rgba(0,0,0,0.75)",
  },
};

export const playButton: SxProps<Theme> = {
  width: { xs: 68, md: 88 },
  height: { xs: 68, md: 88 },
  borderRadius: "50%",
  border: "2px solid rgba(255,255,255,0.85)",
  backdropFilter: "blur(10px)",
  backgroundColor: "rgba(0,0,0,0.55)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "transform 0.3s ease, background-color 0.3s ease",
};

export const testimonialCard: SxProps<Theme> = {
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: "16px",
  p: { xs: 3, md: 4 },
  backdropFilter: "blur(12px)",
  display: "flex",
  flexDirection: "column",
  gap: 3,
};

export const starIcon: SxProps<Theme> = {
  color: "#F4C751",
  fontSize: "1.1rem",
  lineHeight: 1,
};

export const testimonialQuote: SxProps<Theme> = {
  color: "rgba(255,255,255,0.82)",
  fontFamily: "'Barlow', sans-serif",
  fontSize: "0.97rem",
  lineHeight: 1.7,
  flex: 1,
};

export const testimonialAvatar: SxProps<Theme> = {
  width: 44,
  height: 44,
  borderRadius: "50%",
  background: "linear-gradient(135deg, #3b82f6, #38bdf8)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

export const testimonialInitials: SxProps<Theme> = {
  color: "white",
  fontWeight: 700,
  fontSize: "0.8rem",
  fontFamily: "'Poppins', sans-serif",
};

export const testimonialName: SxProps<Theme> = {
  color: "white",
  fontWeight: 600,
  fontSize: "0.9rem",
  fontFamily: "'Poppins', sans-serif",
  lineHeight: 1.3,
};

export const testimonialLocation: SxProps<Theme> = {
  color: "rgba(255,255,255,0.5)",
  fontSize: "0.8rem",
  fontFamily: "'Barlow', sans-serif",
};

export const appSectionOuter: SxProps<Theme> = {
  background: "#0c1120",
  py: { xs: 12, md: 18 },
  display: "flex",
  flexDirection: { xs: "column", md: "row" },
  alignItems: "center",
  overflow: "hidden",
  position: "relative",
};

export const appSectionGlow: SxProps<Theme> = {
  position: "absolute",
  left: 0,
  top: "10%",
  width: "70%",
  height: "80%",
  background:
    "radial-gradient(ellipse 60% 70% at 30% 50%, rgba(56,189,248,0.07) 0%, rgba(59,130,246,0.04) 40%, transparent 70%)",
  pointerEvents: "none",
  zIndex: 0,
};

export const appSectionVideo: SxProps<Theme> = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: "left center",
  display: "block",
};

export const appSectionRight: SxProps<Theme> = {
  flex: 1,
  pl: { xs: 4, md: 8, lg: 12 },
  pr: { xs: 4, md: 6 },
  py: { xs: 6, md: 0 },
  position: "relative",
  zIndex: 1,
};

export const appSectionH2Line1: SxProps<Theme> = {
  fontSize: { xs: "2.5rem", md: "3.5rem", lg: "4rem" },
  fontWeight: 800,
  letterSpacing: "-0.02em",
  lineHeight: 1.15,
  fontFamily: "'Poppins', sans-serif",
  color: "white",
  mb: 0,
};

export const appSectionH2Line2: SxProps<Theme> = {
  fontSize: { xs: "2.5rem", md: "3.5rem", lg: "4rem" },
  fontWeight: 800,
  letterSpacing: "-0.02em",
  lineHeight: 1.15,
  fontFamily: "'Poppins', sans-serif",
  mb: 3,
  ...gradientText,
};

export const appSectionBody: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  color: "rgba(255,255,255,0.82)",
  fontSize: { xs: "1rem", md: "1.1rem" },
  lineHeight: 1.7,
  mb: 4,
};

export const appStoreBadge: SxProps<Theme> = {
  display: "inline-flex",
  alignItems: "center",
  px: 2,
  py: 0.75,
  borderRadius: "100px",
  border: "1px solid rgba(255,255,255,0.15)",
  background: "rgba(255,255,255,0.06)",
  backdropFilter: "blur(8px)",
};

export const appStoreBadgeText: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  fontSize: "0.7rem",
  fontWeight: 600,
  letterSpacing: "0.15em",
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.5)",
};

export const ctaOuter: SxProps<Theme> = {
  py: { xs: 14, md: 20 },
  position: "relative",
  backgroundImage: "url('/img/assets/house2.jpg')",
  backgroundSize: "cover",
  backgroundPosition: "center",
  textAlign: "center",
  overflow: "hidden",
};

export const ctaOverlay: SxProps<Theme> = {
  position: "absolute",
  inset: 0,
  background:
    "radial-gradient(ellipse 70% 80% at 50% 50%, rgba(5,12,35,0.88) 0%, rgba(5,12,35,0.55) 45%, rgba(5,12,35,0.1) 100%)",
  zIndex: 0,
  pointerEvents: "none",
};

export const ctaSectionTitle: SxProps<Theme> = {
  color: "white",
  fontSize: { xs: "2.5rem", md: "3.5rem", lg: "4.5rem" },
  fontWeight: 800,
  letterSpacing: "-0.02em",
  lineHeight: 1.15,
  fontFamily: "'Poppins', sans-serif",
  mb: 3,
};

export const ctaSubtitle: SxProps<Theme> = {
  fontFamily: "'Barlow', sans-serif",
  color: "rgba(255,255,255,0.75)",
  fontSize: { xs: "1rem", md: "1.1rem" },
  lineHeight: 1.7,
  maxWidth: 520,
  mx: "auto",
  mb: 6,
};

export const ctaButtonRow: SxProps<Theme> = {
  display: "flex",
  gap: 2,
  flexWrap: "wrap",
  justifyContent: "center",
};
